/**
 * Notification feature — type → icon + theme maps.
 *
 * Rendered by every row in the bell. Separated out so designers can
 * adjust the icon vocabulary and tint without hunting through
 * `NotificationBell.tsx` or `NotifRow.tsx`.
 *
 * Two exports:
 *   - TYPE_ICON        : preserved legacy ReactNode map (backwards
 *                        compatibility for any caller that just wants
 *                        the pre-rendered glyph).
 *   - TYPE_ICON_THEME  : richer descriptor used by `NotifAvatar` to
 *                        render either a real user avatar or a
 *                        contextual icon+background pairing.
 */

import React from "react";
import {
  AlarmClock,
  Bell,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  Settings,
  UserPlus,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { NotifType } from "../types";

export interface NotifTheme {
  Icon:      LucideIcon;
  bgClass:   string;
  textClass: string;
}

/**
 * Soft background + icon colour for each notification type. All colours
 * are from the Tailwind palette (with 10–15% alpha backgrounds) so they
 * land elegantly in both themes without bespoke dark-mode overrides.
 */
/**
 * Fallback theme used by `getNotifTheme` when a notification row arrives
 * with a `type` that isn't in the known union. Previously the theme
 * lookup returned `undefined` for unknown types and the avatar component
 * crashed on `theme.Icon` — see the production issue triaged in Sprint 2 follow-up
 * (Circles page, NotifAvatar -> PhotoAvatar).
 */
export const FALLBACK_THEME: NotifTheme = {
  Icon:      Bell,
  bgClass:   "bg-muted",
  textClass: "text-muted-foreground",
};

export const TYPE_ICON_THEME: Record<NotifType, NotifTheme> = {
  attendance: {
    Icon:      Calendar,
    bgClass:   "bg-violet-500/12",
    textClass: "text-violet-600 dark:text-violet-300",
  },
  DAILY_REMINDER: {
    Icon:      Calendar,
    bgClass:   "bg-violet-500/12",
    textClass: "text-violet-600 dark:text-violet-300",
  },
  workspace_invite: {
    Icon:      Users,
    bgClass:   "bg-muted",
    textClass: "text-foreground",
  },
  ai_insight: {
    Icon:      Zap,
    bgClass:   "bg-amber-500/15",
    textClass: "text-amber-600 dark:text-amber-300",
  },
  system: {
    Icon:      Settings,
    bgClass:   "bg-muted",
    textClass: "text-muted-foreground",
  },
  BUDDY_INVITE: {
    Icon:      UserPlus,
    bgClass:   "bg-violet-500/12",
    textClass: "text-violet-600 dark:text-violet-300",
  },
  BREAKFAST_INVITE: {
    Icon:      CalendarCheck,
    bgClass:   "bg-orange-500/12",
    textClass: "text-orange-600 dark:text-orange-300",
  },
  // Amber warning tint so the user immediately clocks that this invite
  // has changed since they last looked at it — same colour vocab
  // Google Calendar uses for its "Time updated" banner.
  EVENT_UPDATE: {
    Icon:      CalendarClock,
    bgClass:   "bg-amber-500/15",
    textClass: "text-amber-600 dark:text-amber-300",
  },
  // Red tint + "X" glyph so cancellations read as an error/warning
  // state at a glance — distinguishes them from `EVENT_UPDATE` at the
  // avatar level before the user reads the title.
  EVENT_CANCELLED: {
    Icon:      CalendarX,
    bgClass:   "bg-destructive/12",
    textClass: "text-destructive",
  },
  EVENT_REMINDER: {
    Icon:      AlarmClock,
    bgClass:   "bg-sky-500/12",
    textClass: "text-sky-600 dark:text-sky-300",
  },
};

/**
 * Legacy pre-rendered map kept for callers that just want an icon glyph
 * without the full theme descriptor. New code should prefer the theme
 * map above + `NotifAvatar`.
 *
 * Also handy as a generic fallback in empty states (`<Bell/>`).
 */
export const TYPE_ICON: Record<NotifType, React.ReactNode> = Object.fromEntries(
  (Object.entries(TYPE_ICON_THEME) as [NotifType, NotifTheme][]).map(
    ([key, theme]) => [
      key,
      <theme.Icon key={key} className={`h-4 w-4 ${theme.textClass}`} />,
    ],
  ),
) as Record<NotifType, React.ReactNode>;

export const FALLBACK_ICON = <Bell className="h-4 w-4 text-muted-foreground" />;

/**
 * Defensive accessor — returns the typed theme when we know about the
 * `type`, otherwise a neutral fallback so callers can always assume a
 * `NotifTheme` value. Cast to `unknown` first because TypeScript trusts
 * the `NotifType` union at compile time, but the runtime payload can
 * drift (new DB types rolled out before the client union is updated,
 * cached rows from a previous schema, etc.).
 */
export function getNotifTheme(type: unknown): NotifTheme {
  if (typeof type === "string" && type in TYPE_ICON_THEME) {
    return TYPE_ICON_THEME[type as NotifType];
  }
  return FALLBACK_THEME;
}
